import { StudentTransfer, SubjectGroup } from '../services/subject-group/types/subject-group.type';
import { TransferDirection, TransferNote } from '../services/group/types/transfer-note.type';
import { GroupLinkProgress } from '../services/group/types/group-link-stats.type';
import {
  COURSE_MIN_STUDENTS,
  INTERESTED_LABEL,
  SUBJECT_GROUP_CLASS_TYPE,
} from '../constants/subject-group.constants';

export function groupClassTypes(selected: string[]): string[] {
  return selected.filter((classType) => classType !== SUBJECT_GROUP_CLASS_TYPE);
}

const TRANSFER_TEXT: Record<TransferDirection, (place: string) => string> = {
  SALIDA: (place) => `Trasladado a ${place}`,
  LLEGADA: (place) => `Llegó desde ${place}`,
};

export function transferNoteText(note: TransferNote): string {
  const place = note.groupName ?? INTERESTED_LABEL.toLowerCase();

  return `${TRANSFER_TEXT[note.direction](place)} · ${new Date(note.date).toLocaleDateString('es-ES')}`;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function sinceText(label: string, since: string, today: Date = new Date()): string {
  const start = new Date(since);
  const startDay = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const todayDay = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.max(0, Math.round((todayDay - startDay) / DAY_MS));
  const elapsed = days === 0 ? 'hoy' : days === 1 ? 'hace 1 día' : `hace ${days} días`;

  return `${label} ${start.toLocaleDateString('es-ES')} · ${elapsed}`;
}

export function interestedSinceText(since: string, today: Date = new Date()): string {
  return sinceText('Interesado desde', since, today);
}

export function enrolledSinceText(day: string, today: Date = new Date()): string {
  const [year, month, date] = day.split('-').map(Number);
  return sinceText('En el grupo desde', new Date(year, month - 1, date).toISOString(), today);
}

export function linkSinceText(person: GroupLinkProgress, today: Date = new Date()): string {
  if (person.sharedAt) return sinceText('Enlace compartido el', person.sharedAt, today);

  const firstOpen = (person.attempts ?? []).map((attempt) => attempt.startedAt).sort()[0] ?? person.startedAt;
  return sinceText('Abrió el enlace el', firstOpen, today);
}

export function isTransferHistory(note?: TransferNote | null): boolean {
  return note?.direction === 'SALIDA';
}

interface GroupTransferNote {
  text: string;
  leftByTransfer: boolean;
}

export function transferNotesByGroup(transfers: StudentTransfer[]): Map<number, GroupTransferNote> {
  const notes = new Map<number, GroupTransferNote>();
  const latestFirst = [...transfers].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  for (const transfer of latestFirst) {
    if (transfer.fromGroupId && !notes.has(transfer.fromGroupId)) {
      notes.set(transfer.fromGroupId, {
        text: transferNoteText({ direction: 'SALIDA', groupName: transfer.toGroupName, date: transfer.date }),
        leftByTransfer: true,
      });
    }
    if (transfer.toGroupId && !notes.has(transfer.toGroupId)) {
      notes.set(transfer.toGroupId, {
        text: transferNoteText({ direction: 'LLEGADA', groupName: transfer.fromGroupName, date: transfer.date }),
        leftByTransfer: false,
      });
    }
  }

  return notes;
}

export function planningShortfall(isCourse: boolean, selected: number): string | null {
  if (isCourse && selected < COURSE_MIN_STUDENTS) {
    return `Un curso necesita al menos ${COURSE_MIN_STUDENTS} alumnos y has marcado ${selected}. Marca más o créalo como particular.`;
  }
  if (!selected) return 'Marca al menos un alumno para el grupo.';
  return null;
}

export function toggledIn<T>(set: Set<T>, key: T): Set<T> {
  const next = new Set(set);
  if (!next.delete(key)) next.add(key);
  return next;
}

export function subjectGroupPublicName(subjectGroup: SubjectGroup): string {
  const segments =
    subjectGroup.audience === 'idiomas'
      ? ['Idiomas', languageWithLevel(subjectGroup.languageName ?? subjectGroup.subjectName, subjectGroup.languageLevel)]
      : [subjectGroup.university?.shortName, subjectGroup.subjectName];

  return segments
    .map((segment) => segment?.trim())
    .filter(Boolean)
    .join(' - ');
}

function languageWithLevel(language: string | null | undefined, level: string | null | undefined): string {
  const name = language?.trim() ?? '';
  const hasLevel = !!level && name.split(/\s+/).at(-1) === level.trim();

  return hasLevel || !level ? name : `${name} ${level.trim()}`.trim();
}

export function subjectGroupTeacherIds(subjectGroup: SubjectGroup): number[] | null {
  const ids = [subjectGroup.teacher?.id, ...subjectGroup.teachers.map((teacher) => teacher.id)].filter(
    (id): id is number => id !== undefined && id !== null,
  );
  return ids.length ? [...new Set(ids)] : null;
}
